import axios, { AxiosRequestConfig } from 'axios';
import { httpMethodFunctions } from '../common';

// Axios has no `response` for both a real network failure and a timeout
// (ECONNABORTED) — same underlying "the request never completed" situation
// from the user's point of view, so one message covers both.
export const PAYMENT_NETWORK_ERROR_MESSAGE =
  "We couldn't reach the payment service. Please check your connection and try again.";

interface PaymentErrorResponseBody {
  // FastAPI's manually-raised HTTPExceptions (400/403/409/500) always send a
  // string here. Its automatic Pydantic validation errors (422) send an array
  // of {loc, msg, type} objects instead — guard against that shape below.
  detail?: unknown;
}

export const getPaymentErrorMessage = (
  error: unknown,
  fallbackMessage: string
): string => {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return PAYMENT_NETWORK_ERROR_MESSAGE;
    }
    const body = error.response.data as PaymentErrorResponseBody | undefined;
    return typeof body?.detail === 'string' && body.detail
      ? body.detail
      : fallbackMessage;
  }
  return fallbackMessage;
};

export const toPaymentError = (
  error: unknown,
  fallbackMessage: string
): Error => new Error(getPaymentErrorMessage(error, fallbackMessage));

// Both v1 and v2 payment functions now always throw a real Error with a
// caller-safe .message (v2 via toPaymentError, v1 via withPaymentErrorFallback
// below), so UI call sites can read it unconditionally.
export const resolvePaymentErrorMessage = (
  error: unknown,
  fallbackMessage: string
): string => (error instanceof Error ? error.message : fallbackMessage);

// v1 (str-registration-api) doesn't return a mappable error shape, and its
// timing/retry behavior must stay untouched. This replaces any failure with a
// fixed message — matching the hardcoded string each v1 call site already
// showed before error messages were surfaced — without altering when or how
// v1 requests fail.
export const withPaymentErrorFallback = async <T>(
  operation: () => Promise<T>,
  fallbackMessage: string
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const maintenanceError = getMaintenanceError(error);
    if (maintenanceError) throw maintenanceError;
    throw new Error(fallbackMessage);
  }
};

interface LegacyPaymentErrorResponseBody {
  Message?: string;
  // str-registration-api maintenance 503 shape: {"error": "..."}
  error?: string;
}

// str-registration-api returns {"error": "..."} on 503 maintenance. Surface
// that message rather than the generic fallback so the user understands why.
const getMaintenanceError = (error: unknown): Error | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const response = error.response;
  if (response?.status !== 503) return undefined;
  const body = response.data as LegacyPaymentErrorResponseBody | undefined;
  return body?.error ? new Error(body.error) : undefined;
};

// payGeneric is the one v1 endpoint whose backend sends a message worth
// surfacing: a 409 there means the payment session rolled over into a new
// day, and the response carries a user-friendly explanation. Every other
// failure (and every other v1 endpoint) keeps the fixed-fallback behavior
// above untouched — except maintenance 503s, which are surfaced by both
// wrappers so the user knows the service is temporarily unavailable.
export const withPayGenericErrorFallback = async <T>(
  operation: () => Promise<T>,
  fallbackMessage: string
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const maintenanceError = getMaintenanceError(error);
    if (maintenanceError) throw maintenanceError;
    const response = axios.isAxiosError(error) ? error.response : undefined;
    const body = response?.data as LegacyPaymentErrorResponseBody | undefined;
    if (response?.status === 409 && body?.Message) {
      throw new Error(body.Message);
    }
    throw new Error(fallbackMessage);
  }
};

export const paymentPost = async <TResponse, TBody>(
  url: string,
  body: TBody,
  config: AxiosRequestConfig<TBody>,
  fallbackMessage: string
): Promise<TResponse> => {
  try {
    const { data } = await httpMethodFunctions.post<TBody, TResponse>(
      url,
      body,
      config
    );
    return data;
  } catch (error) {
    throw toPaymentError(error, fallbackMessage);
  }
};

export const paymentGet = async <TResponse>(
  url: string,
  config: AxiosRequestConfig,
  fallbackMessage: string
): Promise<TResponse> => {
  try {
    const { data } = await httpMethodFunctions.get<TResponse>(url, config);
    return data;
  } catch (error) {
    throw toPaymentError(error, fallbackMessage);
  }
};
